// IndexedDB-backed storage for user samples, their audio blobs, and packs.
//
// Schema (v1):
//   samples  — keyPath 'id',         indexed on 'storageKey' + 'createdAt'
//   blobs    — keyPath 'storageKey', one row per unique sha256 (dedup)
//   packs    — keyPath 'id',         indexed on 'slug'
//
// Invariants:
//   - A Sample row may reference a storageKey that has no blob row yet
//     (or that has been deleted). listSamples() never fails on that —
//     upper layers probe getBlob() independently.
//   - putBlob is idempotent: same storageKey → single row. Callers don't
//     need to dedup themselves.
//   - Schema version upgrades are additive. Never remove a store in a
//     migration without a backup / migrate path.

import type { Pack, Sample } from '../types';

export const DB_NAME = 'strudel-harness-samples';
export const DB_VERSION = 1;

export const STORE_SAMPLES = 'samples';
export const STORE_BLOBS = 'blobs';
export const STORE_PACKS = 'packs';

export type BlobRow = {
  storageKey: string;
  blob: Blob;
  byteSize: number;
  mime: string;
  firstSeenAt: number;
};

export type SampleStorage = {
  putSample(sample: Sample): Promise<void>;
  getSample(id: string): Promise<Sample | undefined>;
  listSamples(): Promise<Sample[]>;
  deleteSample(id: string): Promise<void>;
  findSamplesByStorageKey(storageKey: string): Promise<Sample[]>;

  putBlob(storageKey: string, blob: Blob): Promise<void>;
  getBlob(storageKey: string): Promise<Blob | undefined>;
  hasBlob(storageKey: string): Promise<boolean>;

  putPack(pack: Pack): Promise<void>;
  getPack(id: string): Promise<Pack | undefined>;
  listPacks(): Promise<Pack[]>;
  deletePack(id: string): Promise<void>;

  /** Remove blob rows that no sample references. Called on demand —
   *  deleteSample intentionally does NOT cascade, because a sample might
   *  be re-created from a buffer already on disk during the same session. */
  compactBlobs(): Promise<{ removed: number }>;

  close(): void;
};

export async function openSampleStorage(
  dbName: string = DB_NAME,
): Promise<SampleStorage> {
  const db = await openDb(dbName, DB_VERSION);
  return createStorage(db);
}

function openDb(name: string, version: number): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(name, version);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_SAMPLES)) {
        const store = db.createObjectStore(STORE_SAMPLES, { keyPath: 'id' });
        store.createIndex('byStorageKey', 'storageKey', { unique: false });
        store.createIndex('byCreatedAt', 'createdAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_BLOBS)) {
        db.createObjectStore(STORE_BLOBS, { keyPath: 'storageKey' });
      }
      if (!db.objectStoreNames.contains(STORE_PACKS)) {
        const store = db.createObjectStore(STORE_PACKS, { keyPath: 'id' });
        store.createIndex('bySlug', 'slug', { unique: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IDB open failed'));
    req.onblocked = () =>
      reject(new Error('IDB open blocked — close other tabs and retry'));
  });
}

function createStorage(db: IDBDatabase): SampleStorage {
  // Small promise wrappers. One transaction per op keeps the API simple;
  // batch ops can always be added later with a different shape.

  function tx(
    stores: string | string[],
    mode: IDBTransactionMode,
  ): IDBTransaction {
    return db.transaction(stores, mode);
  }

  function req<T>(r: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error ?? new Error('IDB request failed'));
    });
  }

  return {
    async putSample(sample) {
      await req(tx(STORE_SAMPLES, 'readwrite').objectStore(STORE_SAMPLES).put(sample));
    },

    async getSample(id) {
      const result = await req(
        tx(STORE_SAMPLES, 'readonly').objectStore(STORE_SAMPLES).get(id),
      );
      return (result as Sample | undefined) ?? undefined;
    },

    async listSamples() {
      const result = await req(
        tx(STORE_SAMPLES, 'readonly').objectStore(STORE_SAMPLES).getAll(),
      );
      return (result as Sample[]) ?? [];
    },

    async deleteSample(id) {
      await req(
        tx(STORE_SAMPLES, 'readwrite').objectStore(STORE_SAMPLES).delete(id),
      );
    },

    async findSamplesByStorageKey(storageKey) {
      const store = tx(STORE_SAMPLES, 'readonly').objectStore(STORE_SAMPLES);
      const index = store.index('byStorageKey');
      const result = await req(index.getAll(storageKey));
      return (result as Sample[]) ?? [];
    },

    async putBlob(storageKey, blob) {
      // Dedup: short-circuit if already stored. Doing this in a single
      // readwrite transaction avoids a TOCTOU race against concurrent
      // writers, which can happen when two forge tabs import the same
      // file simultaneously.
      const t = tx(STORE_BLOBS, 'readwrite');
      const store = t.objectStore(STORE_BLOBS);
      const existing = await req(store.getKey(storageKey));
      if (existing !== undefined) return;
      const row: BlobRow = {
        storageKey,
        blob,
        byteSize: blob.size,
        mime: blob.type || 'application/octet-stream',
        firstSeenAt: Date.now(),
      };
      await req(store.put(row));
    },

    async getBlob(storageKey) {
      const row = (await req(
        tx(STORE_BLOBS, 'readonly').objectStore(STORE_BLOBS).get(storageKey),
      )) as BlobRow | undefined;
      return row?.blob;
    },

    async hasBlob(storageKey) {
      const key = await req(
        tx(STORE_BLOBS, 'readonly').objectStore(STORE_BLOBS).getKey(storageKey),
      );
      return key !== undefined;
    },

    async putPack(pack) {
      await req(tx(STORE_PACKS, 'readwrite').objectStore(STORE_PACKS).put(pack));
    },

    async getPack(id) {
      const result = await req(
        tx(STORE_PACKS, 'readonly').objectStore(STORE_PACKS).get(id),
      );
      return (result as Pack | undefined) ?? undefined;
    },

    async listPacks() {
      const result = await req(
        tx(STORE_PACKS, 'readonly').objectStore(STORE_PACKS).getAll(),
      );
      return (result as Pack[]) ?? [];
    },

    async deletePack(id) {
      await req(
        tx(STORE_PACKS, 'readwrite').objectStore(STORE_PACKS).delete(id),
      );
    },

    async compactBlobs() {
      // Load every sample's storageKey set, then delete any blob not in it.
      // Done in two transactions (read samples → read+write blobs) because
      // the sample set is bounded and small. For very large libraries a
      // cursor-based scan would be preferable; revisit when users actually
      // hit that scale.
      const samples = (await req(
        tx(STORE_SAMPLES, 'readonly').objectStore(STORE_SAMPLES).getAll(),
      )) as Sample[];
      const referenced = new Set(samples.map((s) => s.storageKey));
      const t = tx(STORE_BLOBS, 'readwrite');
      const store = t.objectStore(STORE_BLOBS);
      const keys = (await req(store.getAllKeys())) as IDBValidKey[];
      let removed = 0;
      for (const k of keys) {
        if (typeof k === 'string' && !referenced.has(k)) {
          store.delete(k);
          removed++;
        }
      }
      await new Promise<void>((resolve, reject) => {
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error ?? new Error('compact failed'));
        t.onabort = () => reject(t.error ?? new Error('compact aborted'));
      });
      return { removed };
    },

    close() {
      db.close();
    },
  };
}
